/** The same QR and CODE128 renderers/options as TripPackageLabelsComponent. */
type QrCallback = (error: unknown, url: string) => void;
type QrConstructor = new (
  element: HTMLElement,
  options: { text: string; width: number; height: number }
) => unknown;
type QrLibrary =
  | QrConstructor
  | {
      toDataURL?: (
        text: string,
        options: { width: number; margin: number },
        callback: QrCallback
      ) => void;
      toCanvas?: (
        canvas: HTMLCanvasElement,
        text: string,
        options: { width: number; margin: number },
        callback: (error: unknown) => void
      ) => void;
    };
export type BarcodeRenderer = (
  svg: SVGElement,
  text: string,
  options: {
    format: string;
    width: number;
    height: number;
    displayValue: boolean;
    margin: number;
  }
) => void;
type LabelWindow = Window & { QRCode?: QrLibrary; JsBarcode?: BarcodeRenderer };
const scripts = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const pending = scripts.get(src);
  if (pending) {
    return pending;
  }
  const loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error('The label barcode renderer could not load. Please retry.'));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    scripts.delete(src);
    throw error;
  });
  scripts.set(src, loading);
  return loading;
}

export async function loadLabelCodeLibraries(): Promise<BarcodeRenderer> {
  const global = window as LabelWindow;
  const qr = global.QRCode;
  const compatibleQr =
    typeof qr === 'function' ||
    typeof qr?.toDataURL === 'function' ||
    typeof qr?.toCanvas === 'function';
  const pendingLibraries: Promise<void>[] = [];
  if (!compatibleQr) {
    pendingLibraries.push(
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js')
    );
  }
  if (!global.JsBarcode) {
    pendingLibraries.push(
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/jsbarcode/3.11.5/JsBarcode.all.min.js')
    );
  }
  await Promise.all(pendingLibraries);
  if (!global.JsBarcode) {
    throw new Error('The label barcode renderer is unavailable.');
  }
  return global.JsBarcode;
}

/** Use an image so the package QR survives cloning into the print window. */
export async function packageQrImage(payload: string): Promise<string> {
  const QRCode = (window as LabelWindow).QRCode;
  if (typeof QRCode === 'function') {
    const target = document.createElement('div');
    new QRCode(target, { text: payload, width: 112, height: 112 });
    const canvas = target.querySelector('canvas');
    if (canvas) {
      return canvas.toDataURL('image/png');
    }
  } else if (QRCode?.toDataURL) {
    return new Promise((resolve, reject) => {
      QRCode.toDataURL!(payload, { width: 112, margin: 0 }, (error, url) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(url);
      });
    });
  } else if (QRCode?.toCanvas) {
    const canvas = document.createElement('canvas');
    await new Promise<void>((resolve, reject) => {
      QRCode.toCanvas!(canvas, payload, { width: 112, margin: 0 }, (error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve();
      });
    });
    return canvas.toDataURL('image/png');
  }
  throw new Error('The package QR code could not be rendered.');
}

export function renderLabelBarcode(
  renderer: BarcodeRenderer,
  svg: SVGElement | undefined,
  text: string
): void {
  if (!text) {
    return;
  }
  if (!svg) {
    throw new Error('The label barcode could not be rendered.');
  }
  renderer(svg, text, {
    format: 'CODE128',
    width: 1.5,
    height: 35,
    displayValue: false,
    margin: 0
  });
  svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
}
